import { useState, useRef, useEffect } from "react";
import {
	Download,
	ChevronDown,
	LayoutDashboard,
	Users,
	Store,
	FileText
} from "lucide-react";
import { Button } from "src/components/ui/button";

export interface ExportDropdownProps {
	onExportDashboard?: () => void;
	onExportUsers?: () => void;
	onExportStores?: () => void;
	onExportObservations?: () => void;
	showDashboard?: boolean;
	showUsers?: boolean;
	showStores?: boolean;
	showObservations?: boolean;
}

const ExportDropdown = ({
	onExportDashboard,
	onExportUsers,
	onExportStores,
	onExportObservations,
	showDashboard = false,
	showUsers = false,
	showStores = false,
	showObservations = false
}: ExportDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const hasAnyOption =
		showDashboard || showUsers || showStores || showObservations;

	if (!hasAnyOption) return null;

	const handleClick = (action?: () => void) => {
		setIsOpen(false);
		action?.();
	};

	const showCsvSection = showUsers || showStores || showObservations;

	return (
		<div className="relative mt-6 mb-64" ref={ref}>
			<Button
				type="button"
				variant="outline"
				className="w-full sm:w-auto"
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<Download className="h-4 w-4" />
				Export
				<ChevronDown
					className={[
						"h-3.5 w-3.5 transition-transform duration-200",
						isOpen ? "rotate-180" : ""
					].join(" ")}
				/>
			</Button>

			{isOpen && (
				<div className="absolute left-0 right-0 sm:right-auto top-full mt-1 sm:min-w-[240px] bg-white border border-border rounded-xl shadow-lg py-1 z-50">
					{showDashboard && (
						<>
							<div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
								Dashboard
							</div>
							<button
								type="button"
								className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-zinc-700 hover:bg-primary-lighter hover:text-primary-dark transition-colors rounded-lg mx-0"
								onClick={() => handleClick(onExportDashboard)}
							>
								<LayoutDashboard className="h-4 w-4 text-zinc-500" />
								Export dashboard
							</button>
						</>
					)}

					{showDashboard && showCsvSection && (
						<div className="my-1 mx-3 border-t border-zinc-100" />
					)}

					{showCsvSection && (
						<>
							<div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
								CSV Reports
							</div>

							{showUsers && (
								<button
									type="button"
									className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-zinc-700 hover:bg-primary-lighter hover:text-primary-dark transition-colors"
									onClick={() => handleClick(onExportUsers)}
								>
									<Users className="h-4 w-4 text-zinc-500" />
									Export all users
								</button>
							)}

							{showStores && (
								<button
									type="button"
									className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-zinc-700 hover:bg-primary-lighter hover:text-primary-dark transition-colors"
									onClick={() => handleClick(onExportStores)}
								>
									<Store className="h-4 w-4 text-zinc-500" />
									Export all stores
								</button>
							)}

							{showObservations && (
								<button
									type="button"
									className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-zinc-700 hover:bg-primary-lighter hover:text-primary-dark transition-colors"
									onClick={() => handleClick(onExportObservations)}
								>
									<FileText className="h-4 w-4 text-zinc-500" />
									Export all observations
								</button>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default ExportDropdown;
